import React, { useEffect, useMemo, useState } from "react";
import { Header } from "@/components/Header";
import { FilterBar } from "@/components/FilterBar";
import { ProductCard } from "@/components/ProductCard";
import { ProductModal } from "@/components/ProductModal";
import { OrderDrawer } from "@/components/OrderDrawer";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { PackageSearch } from "lucide-react";

const PRICE_CAP = 200;

export default function Catalog() {
  const { t } = useApp();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sizes, setSizes] = useState([]);

  const [activeCat, setActiveCat] = useState("");
  const [search, setSearch] = useState("");
  const [activeSize, setActiveSize] = useState("");
  const [maxPrice, setMaxPrice] = useState(PRICE_CAP);
  const [favOnly, setFavOnly] = useState(false);

  const [selected, setSelected] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/sizes").then((r) => setSizes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (activeCat) params.category_id = activeCat;
    if (activeSize) params.size = activeSize;
    if (search) params.search = search;
    if (maxPrice < PRICE_CAP) params.max_price = maxPrice;
    if (favOnly) params.favorites_only = true;
    const h = setTimeout(() => {
      api.get("/products", { params }).then((r) => setProducts(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(h);
  }, [activeCat, activeSize, search, maxPrice, favOnly]);

  const clearFilters = () => {
    setActiveCat("");
    setSearch("");
    setActiveSize("");
    setMaxPrice(PRICE_CAP);
    setFavOnly(false);
  };

  return (
    <div className="min-h-screen">
      <Header onOpenCart={() => setCartOpen(true)} onOpenFavorites={() => { setFavOnly(true); }} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <FilterBar
          categories={categories}
          activeCat={activeCat}
          setActiveCat={setActiveCat}
          search={search}
          setSearch={setSearch}
          sizes={sizes}
          activeSize={activeSize}
          setActiveSize={setActiveSize}
          maxPrice={maxPrice}
          setMaxPrice={setMaxPrice}
          priceCap={PRICE_CAP}
          favOnly={favOnly}
          setFavOnly={setFavOnly}
          onClear={clearFilters}
        />

        <div className="mt-6">
          {products.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-muted-foreground" data-testid="no-products">
              <PackageSearch className="h-16 w-16 mb-4 opacity-40" />
              <p className="text-lg">{t("noProducts")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} onOpen={setSelected} />
              ))}
            </div>
          )}
        </div>
      </main>

      <ProductModal product={selected} open={!!selected} onClose={() => setSelected(null)} />
      <OrderDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
}
